import {API} from "aws-amplify";
import {getAPIInputContext} from "../../../common/utils/apiUtils";
import { WEBSITE_CLIENT_NAME } from "../../../common/constants/constants";
import { TWIN_API } from "../constants/constants";
import {getURLlink} from "../../documentmanager/constants/Constants";
import { downloadFileFromPresignedURL } from "../../../common/utils/s3Utils";
import {EXCALIBUR_FETCH_AUDIT, EXCALIBUR_FETCH_TRANSACTION_DETAILS, EXCALIBUR_GENERATE_REPORT, EXCALIBUR_DOWNLOAD_DOCUMENT, EXCALIBUR_GET_FILE_DOWNLOAD_URL} from "../../../ApiPaths";

// Gap left between successive file downloads for one task. Long enough for the browser to commit
// each download before the next is triggered, short enough that a handful of files still feels
// immediate. Only applies when a task resolves to more than one file.
const MULTI_FILE_DOWNLOAD_STAGGER_MS = 150;

/**
 * This function will fetch paginated Audit for Excalibur
 * @param tableDispatcher
 * @param lastEvaluatedKey Variable used as next startKey for fetching next page of data
 * @param transactionType Variable used to pass transactionType to excalibur fetch audit api
 */
export function getExcaliburAuditData(tableDispatcher, lastEvaluatedKey, transactionType) {
    getAPIInputContext().then(input => {
        input.body.startKey = lastEvaluatedKey;
        input.body.transactionType = transactionType;

        API.post("TWIN_API", EXCALIBUR_FETCH_AUDIT, input).then(resp => {
                if (resp.status === 200) {
                    tableDispatcher({
                        type: "API_RESPONSE",
                        payload: {
                            data: JSON.parse(resp.body.auditData),
                            lastEvaluatedKey: resp.body.lastEvaluatedKey
                        }
                    });
                } else {
                    throw new Error(`EXCALIBUR_FETCH_AUDIT API responded with status ${resp.status}`);
                }
            })
            .catch(error => {
                console.error("EXCALIBUR_FETCH_AUDIT API Error:", error);
                tableDispatcher({ type: "FAILURE_API_RESPONSE", payload: {} });
            });
    });
}

/**
 * This function will fetch Transaction Audit Data
 * @param tableDispatcher
 * @param lastEvaluatedKey Variable used as next startKey for fetching next page of data
 * @param transactionType Variable used to pass transactionType to excalibur fetch audit api
 * @param transactionId Variable used to pass transactionId to excalibur fetch audit api
 * @param clientId Variable used to pass clientId to excalibur fetch audit api
 */
export function getTransactionAuditData(tableDispatcher, lastEvaluatedKey, transactionType, transactionId, clientId) {
    getAPIInputContext().then(input => {
        input.body.startKey = lastEvaluatedKey;
        input.body.transactionType = transactionType;
        input.body.transactionId = transactionId;
        input.body.clientId = clientId;

        API.post("TWIN_API", EXCALIBUR_FETCH_AUDIT, input).then(resp => {
                if (resp.status === 200) {
                    tableDispatcher({
                        type: "API_RESPONSE",
                        payload: {
                            data: JSON.parse(resp.body.auditData),
                            lastEvaluatedKey: resp.body.lastEvaluatedKey
                        }
                    });
                } else {
                    throw new Error(`EXCALIBUR_FETCH_AUDIT by Transaction ID API responded with status ${resp.status}`);
                }
            })
            .catch(error => {
                console.error("EXCALIBUR_FETCH_AUDIT by Transaction ID API Error:", error);
                tableDispatcher({ type: "FAILURE_API_RESPONSE", payload: {} });
            });
    });
}

/**
 * This function will fetch per-task Transaction Details for a given transaction
 * @param tableDispatcher
 * @param startKey Variable used as next startKey (pagination cursor) for fetching next page of data
 * @param transactionId Variable used to pass transactionId to fetch transaction details api
 * @param status Optional status filter to pass to fetch transaction details api
 */
export function getTransactionDetailsData(tableDispatcher, startKey, transactionId, status) {
    getAPIInputContext().then(input => {
        input.body.transactionId = transactionId;
        input.body.startKey = startKey;
        input.body.status = status;

        API.post("TWIN_API", EXCALIBUR_FETCH_TRANSACTION_DETAILS, input).then(resp => {
                if (resp.status === 200) {
                    tableDispatcher({
                        type: "API_RESPONSE",
                        payload: {
                            data: JSON.parse(resp.body.taskData),
                            lastEvaluatedKey: resp.body.lastEvaluatedKey
                        }
                    });
                } else {
                    throw new Error(`EXCALIBUR_FETCH_TRANSACTION_DETAILS API responded with status ${resp.status}`);
                }
            })
            .catch(error => {
                tableDispatcher({ type: "FAILURE_API_RESPONSE", payload: {} });
            });
    });
}

/**
 * This function will put request to generate Excalibur Report
 * @param setData Hook used to update current Audit Data
 * @param setLastEvaluatedKey Hook used to update lastEvaluatedKey
 * @param lastEvaluatedKey Variable used as next startKey for fetching next page of data
 */
export function generateExcaliburReport(s3Key, s3VersionId, useCaseArray,
                                        filename, setSuccess) {

    getAPIInputContext().then(input => {
        input.body.uploadedFileS3Path = s3Key;
        input.body.uploadedFileS3VersionId = s3VersionId;
        input.body.useCase = useCaseArray[0];
        input.body.useCaseType = useCaseArray[1];
        input.body.entityName = filename;
        console.log(input);

        API.post("TWIN_API", EXCALIBUR_GENERATE_REPORT, input).then(resp => {
            console.log(EXCALIBUR_GENERATE_REPORT + " API was called, response : " + resp);
            if (resp.status === 200) {
                setSuccess(() => {
                    return {
                        isVisible: true,
                        type: "success",
                        message: "File Uploaded Successfully. Report Generation in progress"
                    }
                });
            } else if (resp.status === 401) {
                setSuccess(() => {
                    return {
                        isVisible: true,
                        type: "error",
                        message: "User is not authorized to perform this operation"
                    }
                });
            } else {
                setSuccess(() => {
                    return {
                        isVisible: true,
                        type: "error",
                        message: "Some internal error occurred. Please try again in some time."
                    }
                });
            }
        });
    });

}

/**
 * This function will download file URL for Excalibur
 * @param fileName Variable used to download document URL 
 */
export function downloadDocumentBys3PreSignedURL(fileName, useCase, subUseCase, downloadDocumentDispatcher) {
    getAPIInputContext().then(input => {
        input.body.documentName = fileName;
        input.body.clientName = WEBSITE_CLIENT_NAME;
        input.body.requestor = getRequestor(useCase, subUseCase);
        API.post("TWIN_API", EXCALIBUR_DOWNLOAD_DOCUMENT, input).then(resp => {
        console.log(EXCALIBUR_DOWNLOAD_DOCUMENT + " API was called, response : " + resp);
            if (resp.status === 200) {
                getURLlink(resp.documentUrl, fileName);
                downloadDocumentDispatcher({
                    type: "DOWNLOAD_DOCUMENT_SUCCESS", payload: {dispatcher: downloadDocumentDispatcher}
                })
            } else {
                downloadDocumentDispatcher({
                    type: "DOWNLOAD_DOCUMENT_FAILURE", payload: {dispatcher: downloadDocumentDispatcher}
                })
            }
        });
    });
}

/**
 * Fetches the S3 pre-signed URL for a pre-validation failure report and triggers a same-tab
 * download of it via downloadFileFromPresignedURL (which clicks a temporary anchor tag).
 *
 * @param {Object} params
 * @param {string} params.useCase row use case, e.g. "DE"
 * @param {string} params.subUseCase row sub use case, e.g. "VAT"
 * @param {string} params.transactionId Excalibur audit transaction id of the failed request
 * @throws {Error} if the pre-signed URL could not be fetched (caller handles user-facing errors)
 */
export async function downloadErrorReportByTransactionId({useCase, subUseCase, transactionId}) {
    const input = await getAPIInputContext();
    input.body = {
        ...input.body,
        transactionId,
        clientName: WEBSITE_CLIENT_NAME,
        requestor: getRequestor(useCase, subUseCase),
    };
    const resp = await API.post(TWIN_API, EXCALIBUR_DOWNLOAD_DOCUMENT, input);
    if (resp.status !== 200 || !resp.documentUrl) {
        throw new Error(`Failed to fetch error report URL for transactionId: ${transactionId}`);
    }
    downloadFileFromPresignedURL(resp.documentUrl);
}

/**
 * Fetches the S3 pre-signed download URL(s) for one task's file and triggers the download(s).
 *
 * The URL is signed by Excalibur, not by TwinService and not by the browser: TwinService only
 * passes transactionId / taskId / fileType through to Excalibur's GetFileDownloadURL API and
 * returns the URL list unchanged. The browser then downloads straight from S3.
 *
 * @param {Object} params
 * @param {string} params.transactionId Excalibur transaction id (Excalibur's requestId)
 * @param {string} params.taskId per-task identifier of the row being downloaded
 * @param {string} params.fileType one of FILE_TYPE_INPUT_FILE / FILE_TYPE_RECEIPT / FILE_TYPE_OUTPUT_FILE
 * @param {string} [params.clientId] use case identifier, forwarded to Excalibur as its clientId
 * @throws {Error} if no pre-signed URL could be resolved (caller handles the user-facing error)
 */
export async function downloadTaskFile({transactionId, taskId, fileType, clientId}) {
    const input = await getAPIInputContext();
    // getAPIInputContext already sets body.user to the federate username, which TwinService
    // resolves to an alias and authorizes against the TransactionDetails page. Do not overwrite
    // it -- clientId is the tenant and travels in its own field.
    input.body = {
        ...input.body,
        transactionId,
        taskId,
        fileType,
        clientId,
    };
    const resp = await API.post(TWIN_API, EXCALIBUR_GET_FILE_DOWNLOAD_URL, input);
    const fileUrls = resp?.body?.fileUrls;
    if (resp.status !== 200 || !Array.isArray(fileUrls) || fileUrls.length === 0) {
        throw new Error(`Failed to fetch ${fileType} download URL for transactionId: ${transactionId}, taskId: ${taskId}`);
    }
    // An input file path is an S3 prefix, so Excalibur can legitimately return several URLs for
    // one task. Each is triggered on its own macrotask rather than all in one tick: the URLs are
    // signed without a Content-Disposition header, so `downloadFileFromPresignedURL` is a plain
    // cross-origin navigation (the `download` attribute is ignored off-origin), and browsers
    // coalesce navigations queued in the same tick -- which would leave the user with only the
    // last file. Yielding between them lets each start before the next is requested. Nothing is
    // awaited after the final URL, so the single-file case adds no delay at all.
    for (let index = 0; index < fileUrls.length; index++) {
        downloadFileFromPresignedURL(fileUrls[index]);
        if (index < fileUrls.length - 1) {
            await new Promise(resolve => setTimeout(resolve, MULTI_FILE_DOWNLOAD_STAGGER_MS));
        }
    }
}

function getRequestor(useCase, subUseCase) {
    if (useCase === "NA" && subUseCase === "NA") {
        return "Phastos";
    } else if (subUseCase === "NA") {
        return useCase;
    } else {
        return useCase + "_" + subUseCase;
    }
}



